import { useId, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Search } from "lucide-react";
import styles from "./IbicCodeEditor.module.css";

type Option = { value: string; label: string; code?: string };
type Props = {
    options: readonly Option[];
    value: string;
    disabled: boolean;
    onSelect: (value: string) => void;
    label: string;
    hideLabel?: boolean;
    placeholder: string;
    searchPlaceholder: string;
    emptyText: string;
};
export function WorkflowOptionPicker({ options, value, disabled, onSelect, label, hideLabel = false, placeholder, searchPlaceholder, emptyText }: Props) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(-1);
    const input = useRef<HTMLInputElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const choosing = useRef(false);
    const list = useRef<HTMLDivElement>(null);
    const id = useId();
    const optionId = (optionValue: string) => `${id}-${encodeURIComponent(optionValue)}`;
    const selected = options.find(option => option.value === value);
    const matches = options.filter(option => `${option.code ?? ""} ${option.label}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
    const changeOpen = (next: boolean) => {
        if (next) choosing.current = false;
        setOpen(next);
        setQuery("");
        setActive(-1);
    };
    const choose = (nextValue: string) => {
        choosing.current = nextValue !== value;
        changeOpen(false);
        if (nextValue !== value) onSelect(nextValue);
    };
    return (
        <div className={styles.field}>
            <span id={`${id}-label`} className={hideLabel ? styles.visuallyHiddenLabel : styles.label}>
                {label}:
            </span>
            {/* Keep this portaled list inside the active scroll lock when used in a dialog. */}
            <Popover.Root modal open={open && !disabled} onOpenChange={changeOpen}>
                <Popover.Trigger asChild>
                    <button type="button" ref={trigger} disabled={disabled} className={styles.trigger} aria-labelledby={`${id}-label ${id}-value`}>
                        <span id={`${id}-value`} className={styles.value}>
                            {selected ? `${selected.code === undefined ? "" : `${selected.code} `}${selected.label}` : placeholder}
                        </span>
                        <ChevronDown size={16} aria-hidden="true" />
                    </button>
                </Popover.Trigger>
                <Popover.Portal>
                    <Popover.Content
                        className={styles.popover}
                        align="start"
                        sideOffset={6}
                        collisionPadding={12}
                        onCloseAutoFocus={event => {
                            event.preventDefault();
                            if (!choosing.current) trigger.current?.focus();
                        }}
                        onOpenAutoFocus={event => {
                            event.preventDefault();
                            input.current?.focus();
                        }}
                    >
                        <div className={styles.searchBox}>
                            <Search size={20} className={styles.searchIcon} aria-hidden="true" />
                            <input
                                ref={input}
                                className={styles.search}
                                role="combobox"
                                aria-label={searchPlaceholder}
                                placeholder={searchPlaceholder}
                                aria-expanded={open}
                                aria-controls={`${id}-list`}
                                aria-autocomplete="list"
                                aria-activedescendant={active >= 0 && matches[active] ? optionId(matches[active].value) : undefined}
                                value={query}
                                onChange={event => {
                                    setQuery(event.target.value);
                                    setActive(-1);
                                }}
                                onKeyDown={event => {
                                    if (event.key === "Escape") {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        changeOpen(false);
                                    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                                        event.preventDefault();
                                        if (!matches.length) return;
                                        const next = event.key === "ArrowDown" ? (active + 1) % matches.length : active <= 0 ? matches.length - 1 : active - 1;
                                        setActive(next);
                                        list.current?.children[next]?.scrollIntoView({ block: "nearest" });
                                    } else if (event.key === "Enter" && matches[active]) {
                                        event.preventDefault();
                                        choose(matches[active].value);
                                    }
                                }}
                            />
                        </div>
                        <div ref={list} id={`${id}-list`} role="listbox" aria-label={label} className={styles.options}>
                            {matches.map((option, index) => (
                                <button
                                    type="button"
                                    role="option"
                                    tabIndex={-1}
                                    key={option.value}
                                    id={optionId(option.value)}
                                    aria-selected={option.value === value}
                                    data-active={index === active}
                                    className={`${styles.option} ${option.code === undefined ? styles.optionWithoutCode : ""}`}
                                    onClick={() => choose(option.value)}
                                >
                                    {option.code !== undefined && <span className={styles.code}>{option.code}</span>}
                                    <span>{option.label}</span>
                                    {option.value === value && <Check size={20} aria-hidden="true" />}
                                </button>
                            ))}
                        </div>
                        {!matches.length && (
                            <p role="status" className={styles.empty}>
                                {emptyText}
                            </p>
                        )}
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    );
}
